/**
 * The contact route the Support URL is required to offer, as a Netlify Forms submission.
 *
 * Authorised by `docs/marketing/app-store-listing.md` §URLs ("**Support URL** | Required.
 * Must resolve and offer a contact route"). The choice of Netlify Forms over a Netlify
 * Function is a founder decision recorded in `docs/DEVIATIONS.md` — implementation-brief
 * §3 makes Netlify Functions the whole API, and a form handled by the platform is server
 * behaviour outside `functions/`, so it is a deviation rather than an implementation
 * detail.
 *
 * ## Two forms exist, and both are load-bearing
 *
 * Netlify detects forms by parsing the HTML in the publish directory AT DEPLOY TIME. This
 * app is a Vite SPA: the form below does not exist in `apps/web/dist/index.html`, it
 * exists in a JavaScript bundle, so the platform cannot see it and would answer a
 * submission with 404. `apps/web/index.html` therefore carries a hidden static twin whose
 * `name` and field names match this one EXACTLY — that stub is what registers the form,
 * and this component is what a person actually uses. Renaming a field here without
 * renaming it there silently drops that field from every submission.
 *
 * ## Why `fetch` rather than a native submit
 *
 * A native POST lands the visitor on Netlify's own generated success page, which is
 * off-brand, has none of the theme, and is a dead end with no way back. Posting with
 * `fetch` keeps the reader on this page and lets the outcome be said in the catalogue's
 * own voice. The `<form>` is still a real form with real labels, so keyboard submission,
 * autofill and screen readers behave normally; only the transport changes.
 *
 * POSTs go to `/`, not to `/support`. `/` is a real static document in the publish
 * directory; `/support` reaches `index.html` through a `_redirects` rewrite, and a form
 * post is not worth routing through a rewrite when a directly-served path is available.
 *
 * ## Nothing here is red
 *
 * design-direction: "Red is never a state." A failed send is a fact stated in text, in
 * the ordinary text colour, in a live region — not a colour. Both outcomes are announced
 * the same way for the same reason: the reader needs to know what happened, not to be
 * alarmed about it.
 *
 * ## Why `noValidate`, and why the fields are `aria-required` rather than `required`
 *
 * This is not a styling preference. With native `required`, the browser refuses the
 * submission itself and says so in its own transient bubble — which is English-only, in
 * one register, in the browser's voice, and gone on the next keystroke. That is a
 * user-visible string outside `packages/copy`, which design-direction §1 does not permit,
 * and it made `support.form.needEmail` and `support.form.needMessage` unreachable: they
 * were in the catalogue and nothing could ever render them (caught by rendering the page
 * and pressing the button with it empty, not by any test).
 *
 * So the form opts out of native validation and does the check itself, in the catalogue's
 * voice, in the same live region every other outcome uses. `aria-required` keeps the
 * announcement a screen-reader user relies on; `type="email"` stays, because it is what
 * puts an @ on a phone keyboard, and losing that would cost far more than the bubble was
 * worth. Focus moves to the field that needs attention, so the answer to "what now" is
 * where the cursor is.
 */
import { useRef, useState } from 'react';
import type { CopyKey } from '@carn/copy';
import { useCopy } from '../theme/CopyProvider';
import { useTheme } from '../theme/ThemeProvider';
import { text } from './prose';

/** Must match the `name` on the hidden stub form in `apps/web/index.html`. */
const FORM_NAME = 'support';

/**
 * The honeypot field name, matching `netlify-honeypot` on the stub.
 *
 * Netlify's own guidance for Forms is to "prevent spam submissions with a honeypot
 * field". A honeypot rather than a CAPTCHA on purpose: a CAPTCHA is a visual puzzle with
 * a time pressure on the support page of an app for people with executive-function and
 * sensory differences, which is the wrong toll to charge someone who is already writing
 * because something is broken.
 */
const HONEYPOT = 'carn-bot-field';

type Status = { kind: 'idle' | 'sending' | 'sent' } | { kind: 'problem'; messageKey: CopyKey };

export function SupportForm() {
  const theme = useTheme();
  const { t } = useCopy();
  const c = theme.colours;
  const s = theme.space;

  const [email, setEmail] = useState('');
  const [message, setMessage] = useState('');
  const [status, setStatus] = useState<Status>({ kind: 'idle' });
  const emailField = useRef<HTMLInputElement>(null);
  const messageField = useRef<HTMLTextAreaElement>(null);

  async function submit(event: React.FormEvent<HTMLFormElement>) {
    event.preventDefault();

    // An address with no `@` is checked as well as an empty one, and both get the same
    // sentence, because the consequence is identical: the reply goes nowhere and the
    // reader never learns why. Nothing stricter than `@` — every "real" email regex
    // rejects addresses that work, and a support form is the wrong place to be clever.
    if (!email.includes('@') || !email.trim()) {
      emailField.current?.focus();
      return setStatus({ kind: 'problem', messageKey: 'support.form.needEmail' });
    }
    if (!message.trim()) {
      messageField.current?.focus();
      return setStatus({ kind: 'problem', messageKey: 'support.form.needMessage' });
    }

    setStatus({ kind: 'sending' });
    try {
      const body = new URLSearchParams({
        // Netlify matches the submission to the registered form by this field, not by the
        // path posted to. Without it the POST is not a form submission at all.
        'form-name': FORM_NAME,
        email: email.trim(),
        message: message.trim(),
      });
      const response = await fetch('/', {
        method: 'POST',
        headers: { 'Content-Type': 'application/x-www-form-urlencoded' },
        body: body.toString(),
      });
      if (!response.ok) throw new Error(`form submission answered ${response.status}`);
      setStatus({ kind: 'sent' });
      setEmail('');
      setMessage('');
    } catch {
      // The text a reader gets says "try again in a moment" and nothing about the cause:
      // a status code is not information they can act on, and the local draft is not lost
      // — the fields still hold what they typed.
      setStatus({ kind: 'problem', messageKey: 'support.form.failed' });
    }
  }

  const fieldStyle = {
    ...text(theme.type.body),
    fontFamily: theme.type.family,
    minHeight: s.minTarget,
    width: '100%',
    boxSizing: 'border-box' as const,
    padding: `${s.sm}px ${s.md}px`,
    borderRadius: s.radius.md,
    border: `${s.hairline}px solid ${c.borderStrong}`,
    background: c.surface,
    color: c.text,
  };

  const labelStyle = { ...text(theme.type.label), color: c.text, fontFamily: theme.type.family };

  return (
    <form
      name={FORM_NAME}
      onSubmit={submit}
      // See this file's header: the browser's own validation copy is not in the catalogue,
      // so this form does the checking and says so in its own voice.
      noValidate
      style={{ display: 'flex', flexDirection: 'column', gap: s.md }}
    >
      <div style={{ display: 'flex', flexDirection: 'column', gap: s.xs }}>
        <label htmlFor="support-email" style={labelStyle}>
          {t('support.form.emailLabel')}
        </label>
        <input
          id="support-email"
          ref={emailField}
          name="email"
          type="email"
          autoComplete="email"
          aria-required="true"
          aria-describedby="support-email-hint"
          value={email}
          onChange={(e) => setEmail(e.target.value)}
          style={fieldStyle}
        />
        <p
          id="support-email-hint"
          style={{ ...text(theme.type.quiet), color: c.textTertiary, margin: 0 }}
        >
          {t('support.form.emailHint')}
        </p>
      </div>

      <div style={{ display: 'flex', flexDirection: 'column', gap: s.xs }}>
        <label htmlFor="support-message" style={labelStyle}>
          {t('support.form.messageLabel')}
        </label>
        <textarea
          id="support-message"
          ref={messageField}
          name="message"
          aria-required="true"
          rows={6}
          placeholder={t('support.form.messagePlaceholder')}
          value={message}
          onChange={(e) => setMessage(e.target.value)}
          style={{ ...fieldStyle, minHeight: s.minTarget * 3, resize: 'vertical' }}
        />
      </div>

      {/* The honeypot. Hidden from everyone — including assistive technology, which is why
          `aria-hidden` and `tabIndex={-1}` are both here: a screen-reader user must not be
          offered a field whose only purpose is to be left empty. */}
      <div style={{ display: 'none' }} aria-hidden="true">
        <label htmlFor="support-honeypot">{HONEYPOT}</label>
        <input id="support-honeypot" name={HONEYPOT} type="text" tabIndex={-1} />
      </div>

      <button
        type="submit"
        disabled={status.kind === 'sending'}
        style={{
          ...text(theme.type.label),
          fontFamily: theme.type.family,
          minHeight: s.minTarget,
          padding: `0 ${s.lg}px`,
          borderRadius: s.radius.md,
          border: `${s.hairline}px solid ${c.borderStrong}`,
          background: c.accent,
          color: c.onAccent,
          alignSelf: 'flex-start',
          cursor: status.kind === 'sending' ? 'default' : 'pointer',
        }}
      >
        {t(status.kind === 'sending' ? 'support.form.sending' : 'support.form.submit')}
      </button>

      {/* One live region for every outcome, so a change of status is announced once and
          the reader is never left guessing whether the press registered. `role="status"`
          rather than `role="alert"` even for a failure — an alert is an interruption, and
          a send that needs retrying is not an emergency. */}
      <p
        role="status"
        aria-live="polite"
        style={{ ...text(theme.type.body), color: c.text, margin: 0, minHeight: theme.type.body.lineHeight }}
      >
        {status.kind === 'sent' ? t('support.form.sent') : null}
        {status.kind === 'problem' ? t(status.messageKey) : null}
      </p>
    </form>
  );
}
